import {
  Button,
  HStack,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
} from "@chakra-ui/react";
import { addMonths } from "date-fns";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import React, { useState, useEffect, useCallback, forwardRef } from "react";
import {
  DateRange as DayPickerDateRange,
  DayPicker,
  TZDate,
} from "react-day-picker";
import { DateRangePickerPreset, DatePickerRange } from "./DateRangePicker";
import { DateRangePresetsDropdown } from "./DateRangePresetsDropdown";
import { DayPickerChevron } from "./DayPickerChevron";
import { DayPickerDropdown } from "./DayPickerDropdown";
import {
  getNextDateRangeSelection,
  CalendarSide,
} from "./getNextDateRangeSelection";
import { toTZDate, fromTZDate } from "./tzDateUtils";

type DateRangePickerDualPopoverContentProps = {
  dateRange?: DatePickerRange;
  earliestDate?: Date;
  latestDate?: Date;
  presetTitle: string;
  presetDateRanges?: DateRangePickerPreset[];
  onSelectRange: (dateRange: DatePickerRange | undefined) => void;
  onClear: () => void;
  onClose: () => void;
  timeZone: string;
};

// the current date range selected
type PickerDateRange = {
  from: TZDate;
  to: TZDate;
};

// the date defining which month is visible in the left and right calendar,
// always set to a 'start of month' date.
type VisibleMonths = {
  leftMonth: TZDate;
  rightMonth: TZDate;
};

/**
 * A date range picker displayed with dual calendars for selecting date ranges
 * spanning multiple months / years.
 */
export const DateRangePickerDualPopoverContent = forwardRef<
  HTMLDivElement,
  DateRangePickerDualPopoverContentProps
>(
  (
    {
      dateRange,
      earliestDate,
      latestDate,
      presetTitle,
      presetDateRanges,
      onSelectRange,
      onClear,
      onClose,
      timeZone,
    },
    ref,
  ) => {
    const pickerEarliestDate =
      earliestDate ? toTZDate(earliestDate, timeZone) : undefined;

    const pickerLatestDate =
      latestDate ? toTZDate(latestDate, timeZone) : undefined;

    const disabledDays = [
      ...(pickerEarliestDate ? [{ before: pickerEarliestDate }] : []),
      ...(pickerLatestDate ? [{ after: pickerLatestDate }] : []),
    ];

    const [visibleMonths, setVisibleMonths] = useState<VisibleMonths>(() => {
      const pickerDateRange =
        dateRange ?
          {
            from: toTZDate(dateRange.from, timeZone),
            to: toTZDate(dateRange.to, timeZone),
          }
        : undefined;

      return initialiseVisibleMonths(
        timeZone,
        pickerDateRange,
        pickerEarliestDate,
        pickerLatestDate,
      );
    });

    // the dateRange can change from an external source (e.g. the preset menu),
    // which we force here to update the left and right calendar months.
    useEffect(() => {
      if (!dateRange) {
        return;
      }

      const nextVisibleMonths = initialiseVisibleMonths(
        timeZone,
        {
          from: toTZDate(dateRange.from, timeZone),
          to: toTZDate(dateRange.to, timeZone),
        },
        earliestDate ? toTZDate(earliestDate, timeZone) : undefined,
        latestDate ? toTZDate(latestDate, timeZone) : undefined,
      );

      setVisibleMonths((current) => {
        if (
          current.leftMonth.getTime() ===
            nextVisibleMonths.leftMonth.getTime() &&
          current.rightMonth.getTime() ===
            nextVisibleMonths.rightMonth.getTime()
        ) {
          return current;
        }

        return nextVisibleMonths;
      });
    }, [timeZone, dateRange, earliestDate, latestDate]);

    const handleSelect = useCallback(
      (side: CalendarSide) =>
        (_range: DayPickerDateRange | undefined, selectedDay: Date) => {
          const nextDateRange = getNextDateRangeSelection({
            selectedDay: selectedDay as TZDate,
            dateRange,
            timeZone,
            calendarSide: side,
          });
          onSelectRange(nextDateRange);
        },
      [dateRange, timeZone, onSelectRange],
    );

    const handleLeftMonthChange = useCallback(
      (month: Date) => {
        setVisibleMonths((current) =>
          normaliseVisibleMonths(
            {
              side: "left",
              date: month as TZDate,
            },
            current,
            timeZone,
            pickerEarliestDate,
            pickerLatestDate,
          ),
        );
      },
      [pickerEarliestDate, pickerLatestDate, timeZone],
    );

    const handleRightMonthChange = useCallback(
      (month: Date) => {
        setVisibleMonths((current) =>
          normaliseVisibleMonths(
            {
              side: "right",
              date: month as TZDate,
            },
            current,
            timeZone,
            pickerEarliestDate,
            pickerLatestDate,
          ),
        );
      },
      [pickerEarliestDate, pickerLatestDate, timeZone],
    );

    return (
      <PopoverContent
        zIndex="dropdown"
        ref={ref}
        w="auto"
        maxW="100vw"
        bg="white"
      >
        <PopoverBody>
          <HStack align="start" spacing={4}>
            <DayPicker
              mode="range"
              animate={true}
              timeZone={timeZone}
              selected={
                dateRange ?
                  {
                    from: toTZDate(dateRange.from, timeZone),
                    to: toTZDate(dateRange.to, timeZone),
                  }
                : undefined
              }
              onSelect={handleSelect("left")}
              disabled={disabledDays}
              captionLayout="dropdown"
              navLayout="after"
              startMonth={pickerEarliestDate}
              endMonth={pickerLatestDate}
              reverseYears
              fixedWeeks
              showOutsideDays
              components={{
                Chevron: DayPickerChevron,
                Dropdown: DayPickerDropdown,
              }}
              month={visibleMonths.leftMonth}
              onMonthChange={handleLeftMonthChange}
              numberOfMonths={1}
            />

            <DayPicker
              mode="range"
              animate={true}
              timeZone={timeZone}
              selected={
                dateRange ?
                  {
                    from: toTZDate(dateRange.from, timeZone),
                    to: toTZDate(dateRange.to, timeZone),
                  }
                : undefined
              }
              onSelect={handleSelect("right")}
              disabled={disabledDays}
              captionLayout="dropdown"
              navLayout="after"
              startMonth={pickerEarliestDate}
              endMonth={pickerLatestDate}
              reverseYears={true}
              fixedWeeks={true}
              showOutsideDays={true}
              components={{
                Chevron: DayPickerChevron,
                Dropdown: DayPickerDropdown,
              }}
              month={visibleMonths.rightMonth}
              onMonthChange={handleRightMonthChange}
              numberOfMonths={1}
            />
          </HStack>
        </PopoverBody>

        <PopoverFooter>
          <HStack justify="space-between" spacing={3} flexWrap="wrap">
            {presetDateRanges && presetDateRanges.length > 0 && (
              <DateRangePresetsDropdown
                value={dateRange}
                title={presetTitle}
                presetDateRanges={presetDateRanges}
                onSelect={onSelectRange}
              />
            )}

            <HStack>
              {dateRange && (
                <Button
                  size="sm"
                  colorScheme="error"
                  variant="ghost"
                  onClick={onClear}
                >
                  Clear
                </Button>
              )}

              {!dateRange && (
                <Button
                  size="sm"
                  colorScheme="gray"
                  variant="outline"
                  onClick={onClose}
                >
                  Close
                </Button>
              )}

              {dateRange && (
                <Button
                  size="sm"
                  colorScheme="primary"
                  variant="solid"
                  onClick={onClose}
                >
                  Done
                </Button>
              )}
            </HStack>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    );
  },
);

function initialiseVisibleMonths(
  timeZone: string,
  dateRange?: PickerDateRange,
  earliestDate?: TZDate,
  latestDate?: TZDate,
): VisibleMonths {
  // if no date range has been selected, then initialise to the current date.
  if (!dateRange) {
    const rightMonth = clampMonth(
      startOfMonth(TZDate.tz(timeZone)),
      earliestDate,
      latestDate,
    );

    return {
      leftMonth: addMonthsInTimezone(rightMonth, -1, timeZone),
      rightMonth: rightMonth,
    };
  }

  // if a date range was provided on initialisation, then we pretend the
  // left calendar was just selected, and update like any other selection.
  return normaliseVisibleMonths(
    {
      side: "left",
      date: dateRange.from,
    },
    {
      leftMonth: startOfMonth(dateRange.from),
      rightMonth: startOfMonth(dateRange.to),
    },
    timeZone,
    earliestDate,
    latestDate,
  );
}

function normaliseVisibleMonths(
  newDate: {
    side: "left" | "right";
    date: TZDate;
  },
  currentlyVisible: VisibleMonths,
  timeZone: string,
  earliestDate?: TZDate,
  latestDate?: TZDate,
): VisibleMonths {
  let nextLeftMonthVisible =
    newDate.side === "left" ?
      clampMonth(startOfMonth(newDate.date), earliestDate, latestDate)
    : currentlyVisible.leftMonth;

  let nextRightMonthVisible =
    newDate.side === "right" ?
      clampMonth(startOfMonth(newDate.date), earliestDate, latestDate)
    : currentlyVisible.rightMonth;

  // if both sides have the same month, or they somehow invert, then we must
  // push both sides around so they make sense.
  if (nextLeftMonthVisible.getTime() >= nextRightMonthVisible.getTime()) {
    // if the left side was the one adjusted, then we push the right side forward
    if (newDate.side === "left") {
      nextRightMonthVisible = clampMonth(
        addMonthsInTimezone(nextLeftMonthVisible, 1, timeZone),
        earliestDate,
        latestDate,
      );

      // if it didn't help (because the right month was already at the latest
      // allowed, then the left month is pushed back (so it doesn't change)
      if (nextLeftMonthVisible.getTime() >= nextRightMonthVisible.getTime()) {
        nextLeftMonthVisible = clampMonth(
          addMonthsInTimezone(nextLeftMonthVisible, -1, timeZone),
          earliestDate,
          latestDate,
        );
      }
    }

    // if the right side was the one adjusted, then we push the left side back
    else {
      nextLeftMonthVisible = clampMonth(
        addMonthsInTimezone(nextRightMonthVisible, -1, timeZone),
        earliestDate,
        latestDate,
      );

      // if it didn't help (because the left month was already at the earliest
      // allowed, then the right month is pushed forward (so it doesn't change)
      if (nextLeftMonthVisible.getTime() >= nextRightMonthVisible.getTime()) {
        nextRightMonthVisible = clampMonth(
          addMonthsInTimezone(nextRightMonthVisible, 1, timeZone),
          earliestDate,
          latestDate,
        );
      }
    }
  }

  return {
    leftMonth: nextLeftMonthVisible,
    rightMonth: nextRightMonthVisible,
  };
}

function clampMonth(
  month: TZDate,
  earliestDate?: TZDate,
  latestDate?: TZDate,
): TZDate {
  if (earliestDate && compareMonths(month, earliestDate) < 0) {
    return startOfMonth(earliestDate);
  }

  if (latestDate && compareMonths(month, latestDate) > 0) {
    return startOfMonth(latestDate);
  }

  return month;
}

function compareMonths(left: TZDate, right: TZDate): number {
  const yearDiff = left.getFullYear() - right.getFullYear();

  if (yearDiff !== 0) {
    return yearDiff;
  }

  return left.getMonth() - right.getMonth();
}

function startOfMonth(date: TZDate): TZDate {
  return new TZDate(date.getFullYear(), date.getMonth(), date.timeZone);
}

function addMonthsInTimezone(
  date: TZDate,
  months: number,
  timeZone: string,
): TZDate {
  const utcDate = fromTZDate(date);
  const zoned = utcToZonedTime(utcDate, timeZone);
  const addMonth = addMonths(zoned, months);
  const newUtc = zonedTimeToUtc(addMonth, timeZone);
  return toTZDate(newUtc, timeZone);
}
